import { useCallback, useContext, ReactNode } from 'react'
import styled from 'styled-components'
import Media from 'components/utils/Media'
import useEmblaCarousel from 'embla-carousel-react'

import { breakpoints, media, mediaMax } from 'styles/media'

import TouchDeviceContext from 'contexts/TouchDeviceContext'

import useBufferedSlideCount from '../../../hooks/useBufferedSlideCount'
import useEmblaNavigation from '../../../hooks/useEmblaNavigation'
import { useGetSelectEscapeIndexesQuery } from '../../../hooks/useQueries'

import ChevronDownIcon from '../../../components/icons/ChevronDown'

import AdventureCardSlider from './AdventureCardSlider'
import AdventureCardList from './AdventureCardList'

const BREAKPOINT = 'lg' as const

const MobileOnly = styled.div`
  ${media[BREAKPOINT]`
    display: none;
  `}
`

const DesktopOnly = styled.div`
  ${mediaMax[BREAKPOINT]`
    display: none;
  `}
`

const Arrow = styled.button.attrs({ type: 'button' })<{
  left?: boolean
  right?: boolean
  disabled?: boolean
}>`
  height: 40px;
  width: 40px;
  padding: 0;

  ${({ left }) =>
    left &&
    `
    left: 8px;

    svg {
      margin-left: -2px;
    }
  `}

  ${({ right }) =>
    right &&
    `
    right: 8px;
    svg {
      margin-right: -2px;
    }
  `}

  appearance: none;
  outline: 0;
  border: none;
  color: ${({ theme }) => theme.brand.forest};
  background-color: white;

  svg {
    display: inline-block;
    vertical-align: middle;
    height: 13px;
    margin-top: -2px;
    transform: rotate(${({ left }) => (left ? '90deg' : '-90deg')});
  }

  ${({ disabled }) =>
    disabled &&
    `
    color: #999;
  `}
`

const Inner = styled.div``

const Intro = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  line-height: 1.1;

  color: ${({ theme }) => theme.brand.forest};
`

const Empty = styled.div`
  color: ${({ theme }) => theme['-ds-core-300'].value};
  font-size: ${({ theme }) => theme['-ds-font-size-md']['font-size'].value};
`

type AdventureEdge = {
  node?: GQL_AdventureCardFragmentFragment
}

type SuggestedAdventuresProps = {
  adventures: { edges?: AdventureEdge[] }
  inItineraryContext?: boolean
  canFetchMore?: boolean
  customTitle?: ReactNode
  handleFetchMore: (...args: any) => any
  loading?: boolean
  isEmpty?: boolean
  maxSlidesDesktop?: number
  showReviewScore?: boolean
}

const SuggestedAdventures = ({
  adventures,
  inItineraryContext,
  canFetchMore,
  customTitle,
  handleFetchMore,
  loading,
  isEmpty,
  maxSlidesDesktop,
  showReviewScore,
}: SuggestedAdventuresProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    slidesToScroll: 'auto',
    containScroll: 'trimSnaps',
  })

  const onReachEnd = useCallback(() => {
    if (canFetchMore && !loading) handleFetchMore()
  }, [canFetchMore, loading, handleFetchMore])

  const {
    handlePrev,
    handleNext,
    prevDisabled,
    nextDisabled,
  } = useEmblaNavigation(emblaApi, { onReachEnd })
  const isTouchDevice = useContext(TouchDeviceContext)

  const { data: selectEscapeIndexesData } = useGetSelectEscapeIndexesQuery()
  const bucketListItemsIndex = selectEscapeIndexesData?.currentUser
    ?.bucketListItemsIndex as GQL_BucketListItemsIndex
  const bookingAdventuresIndex = selectEscapeIndexesData?.currentUser
    ?.bookingAdventuresIndex as GQL_BookingAdventuresIndex

  const adventureNodes = adventures?.edges?.map(({ node }) => node)
  const totalSlides = adventureNodes?.length || 0
  const slideCount = useBufferedSlideCount(totalSlides, emblaApi)

  return (
    <Media query={`(min-width: ${breakpoints.xl}px)`}>
      {(isDesktop: boolean) => (
        <Inner>
          <Intro>
            {customTitle || <Title>Suggested Things to Do</Title>}

            {(!isTouchDevice || isDesktop) && !isEmpty && (
              <div>
                <Arrow
                  left
                  onClick={handlePrev}
                  disabled={prevDisabled}
                  aria-label="Previous slide"
                >
                  <ChevronDownIcon />
                </Arrow>
                <Arrow
                  right
                  onClick={handleNext}
                  disabled={nextDisabled}
                  aria-label="Next slide"
                >
                  <ChevronDownIcon />
                </Arrow>
              </div>
            )}
          </Intro>

          {isEmpty && <Empty>Coming Soon!</Empty>}

          {!isEmpty && (
            <>
              <DesktopOnly>
                <AdventureCardSlider
                  emblaRef={emblaRef}
                  adventures={adventureNodes}
                  slideCount={slideCount}
                  inItineraryContext={inItineraryContext}
                  maxSlides={maxSlidesDesktop}
                  showReviewScore={showReviewScore}
                  bookingAdventuresIndex={bookingAdventuresIndex}
                  bucketListItemsIndex={bucketListItemsIndex}
                />
              </DesktopOnly>

              <MobileOnly>
                <AdventureCardList
                  loading={loading}
                  canFetchMore={canFetchMore}
                  onFetchMore={handleFetchMore}
                  adventures={adventureNodes}
                  inItineraryContext={inItineraryContext}
                  bookingAdventuresIndex={bookingAdventuresIndex}
                  bucketListItemsIndex={bucketListItemsIndex}
                  showReviewScore={showReviewScore}
                />
              </MobileOnly>
            </>
          )}
        </Inner>
      )}
    </Media>
  )
}

export default SuggestedAdventures
